// mindzera-frontend/src/Builder.jsx
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useAuth } from './AuthContext.jsx';
import { flattenTree, treeify } from './builderUtils.js';

export default function Builder() {
  /* ---------------- auth & api ---------------- */
  const { user, api } = useAuth();        // api auto-injects JWT
  const [mapId, setMapId] = useState(null);

  /* ---------------- UI state ------------------ */
  const [domains, setDomains] = useState([]);
  const [sel, setSel] = useState({ domain:null, skill:null, subskill:null });

  /* ---------------- load map on login --------- */
  useEffect(() => {
    if (!user) return;

    api.get('/api/maps')                       // list maps
       .then(r =>
         r.data.length
           ? r.data[0]                         // use first map
           : api.post('/api/maps').then(r => r.data)   // or create
       )
       .then(m => {
         setMapId(m.id);
         return api.get(`/api/maps/${m.id}/nodes`);
       })
       .then(r => setDomains(treeify(r.data)))
       .catch(console.error);
  }, [user]);

  /* ---------------- auto-save with debounce --- */
  useEffect(() => {
    if (!mapId) return;
    const h = setTimeout(() => {
      api.put(`/api/maps/${mapId}/nodes`, flattenTree(domains))
         .catch(console.error);
    }, 600);                 // 0.6-s debounce
    return () => clearTimeout(h);
  }, [domains, mapId]);

  /* ---------- CRUD helpers (unchanged UI) ----- */
  const addItem = (level) => {
    const name = prompt(`New ${level} name?`);
    if (!name) return;

    if (level === 'domain') {
      setDomains([...domains, { id: uuid(), name, children: [] }]);
    }
    if (level === 'skill') {
      setDomains(
        domains.map(d =>
          d.id === sel.domain
            ? { ...d, children: [...d.children, { id: uuid(), name, children: [] }] }
            : d
        )
      );
    }
    if (level === 'subskill') {
      setDomains(
        domains.map(d =>
          d.id !== sel.domain ? d : {
            ...d,
            children: d.children.map(s =>
              s.id === sel.skill
                ? { ...s, children: [...s.children,
                                    { id: uuid(), name, children: [] }] }
                : s
            )
          }
        )
      );
    }
    if (level === 'microskill') {
      setDomains(
        domains.map(d =>
          d.id !== sel.domain ? d : {
            ...d,
            children: d.children.map(s =>
              s.id !== sel.skill ? s : {
                ...s,
                children: s.children.map(ss =>
                  ss.id === sel.subskill
                    ? { ...ss, children: [...ss.children, { id: uuid(), name, children: [] }] }
                    : ss
                )
              }
            )
          }
        )
      );
    }
  };

  /* ---------- render helpers ------------------ */
  const Col = ({ title, items, level, selectedId, onSelect, canAdd }) => (
    <div className="flex-1 border-r last:border-r-0 px-4">
      <h3 className="font-semibold mb-2">{title}</h3>
      <ul className="space-y-1 mb-4">
        {items.map(it => (
          <li key={it.id}
              onClick={() => onSelect(it.id)}
              className={'cursor-pointer px-2 py-1 rounded ' +
                (it.id === selectedId
                  ? 'bg-emerald-200 text-emerald-800'
                  : 'hover:bg-gray-100')}>
            {it.name}
          </li>
        ))}
      </ul>
      {canAdd && (
        <button onClick={() => addItem(level)}
                className="text-sm text-blue-600 hover:underline">
          + Add {title.slice(0, -1)}
        </button>
      )}
    </div>
  );

  /* ---------- pick selected nodes -------------- */
  const selDomain   = domains.find(d => d.id === sel.domain);
  const selSkill    = selDomain?.children.find(s => s.id === sel.skill);
  const selSubskill = selSkill?.children.find(ss => ss.id === sel.subskill);

  return (
    <section className="p-6">
      <h1 className="text-2xl font-bold mb-6">Microskill Builder</h1>

      {!user && <p>Please log in…</p>}
      {user && (
        <div className="flex bg-white shadow rounded-lg overflow-hidden">
          <Col title="Domains"    items={domains}
               level="domain"
               selectedId={sel.domain}
               onSelect={id=>setSel({ domain:id,skill:null,subskill:null })}
               canAdd />
          <Col title="Skills"     items={selDomain?.children || []}
               level="skill"
               selectedId={sel.skill}
               onSelect={id=>setSel({ ...sel, skill:id, subskill:null })}
               canAdd={!!sel.domain} />
          <Col title="Subskills"  items={selSkill?.children || []}
               level="subskill"
               selectedId={sel.subskill}
               onSelect={id=>setSel({ ...sel, subskill:id })}
               canAdd={!!sel.skill} />
          <Col title="Microskills" items={selSubskill?.children || []}
               level="microskill"
               selectedId={null}
               onSelect={()=>{}}
               canAdd={!!sel.subskill} />
        </div>
      )}
    </section>
  );
}
